/**
 * THE ACTIVATION ENGINE
 * ===========================================================================
 * Behavioral activation, as a function of how someone is right now.
 *
 * BA's active ingredient is not "exercise". It is scheduling a CONCRETE,
 * ACHIEVABLE action and doing it regardless of how you feel — then noticing
 * what it did. Two failure modes kill it in practice, and both are design
 * problems, not clinical ones:
 *
 *   1. THE ASK IS TOO BIG. A depressed patient handed "go to the gym" fails,
 *      and the failure confirms the belief the treatment is trying to
 *      disconfirm. The ask must be sized to today's floor, not to a goal.
 *   2. THE ASK IS TOO VAGUE. "Do something you enjoy" is unactionable when
 *      anhedonia is the presenting symptom. The action has to be nameable in
 *      five words and startable in under a minute.
 *
 * So this module never returns advice. It returns a small set of specific,
 * already-decided actions, sized to the patient's current state, of which the
 * patient picks one. Choosing from three is a decision a person at mood 1 can
 * still make; generating one from nothing is not.
 *
 * WHY THIS IS SEPARATE FROM prescriptionFor()
 * utils/mood.js is the fitness prescription and is spoken for by its own tests
 * and surfaces. This is the clinical vocabulary: movement is still the
 * highest-evidence option and stays the default, but "make one hot drink" is a
 * legitimate activation on a day when a walk is not, and a tool that can only
 * prescribe exercise has no answer for the worst day it will ever see.
 *
 * ACTIVATION KINDS
 *   movement    highest evidence base; default where state allows
 *   care        self-maintenance; the floor when nothing else is reachable
 *   connection  social contact; potent, and the first thing depression removes
 *   mastery     small completions; rebuilds the sense of agency
 *
 * Pure. No dates, no DOM, no storage — everything here is testable.
 */

/** The floor. Nothing in this tier may exceed ten minutes or require anyone else. */
const FLOOR = [
  { id: "outside-5", label: "Step outside for five minutes", minutes: 5, kind: "movement" },
  { id: "drink", label: "Make a hot drink and sit with it", minutes: 5, kind: "care" },
  { id: "walk-10", label: "Walk once around the block", minutes: 10, kind: "movement" },
];

const DISCHARGE = [
  { id: "walk-brisk", label: "Walk fast for twenty minutes", minutes: 20, kind: "movement" },
  { id: "cardio", label: "Anything that gets you breathing hard", minutes: 20, kind: "movement" },
  { id: "tidy", label: "Clear one surface, start to finish", minutes: 15, kind: "mastery" },
];

const EASY = [
  { id: "walk-20", label: "Twenty-minute walk", minutes: 20, kind: "movement" },
  { id: "text", label: "Message one person back", minutes: 5, kind: "connection" },
  { id: "one-thing", label: "One thing off the list, the smallest", minutes: 15, kind: "mastery" },
];

const STANDARD = [
  { id: "walk-30", label: "Thirty minutes outside", minutes: 30, kind: "movement" },
  { id: "session", label: "A full session — whatever you usually do", minutes: 30, kind: "movement" },
  { id: "call", label: "Call someone for ten minutes", minutes: 10, kind: "connection" },
];

const CAPACITY = [
  { id: "session-full", label: "A full session — whatever you usually do", minutes: 45, kind: "movement" },
  { id: "see-someone", label: "See someone in person", minutes: 60, kind: "connection" },
  { id: "project", label: "An hour on something you've been putting off", minutes: 60, kind: "mastery" },
];

/**
 * What today asks for.
 *
 * `why` is addressed to the patient and must survive being read on the worst
 * day of their year: it explains the SIZE of the ask, never justifies it by
 * appeal to effort, goals or catching up.
 *
 * `ceiling` is the contract this tier makes with the patient — no option in the
 * tier may exceed it. Enforced by test, because the entire safety of this
 * mechanism rests on the floor tier staying small.
 */
export function activationFor(mood, energy) {
  if (mood == null || energy == null) return null;

  /**
   * MOOD OVERRIDES ENERGY AT THE BOTTOM OF THE SCALE.
   *
   * This gate used to read `mood <= 2 && energy <= 2`, which meant a patient
   * reporting the very bottom of the mood scale with middling energy fell
   * through to a twenty-minute walk, and one reporting themselves restless
   * fell through to hard cardio — the exact failure this file opens by warning
   * against. Self-reported energy on a day like that is not capacity; it is
   * frequently agitation, and it is the least reliable number the app holds.
   *
   * At mood 1 the ask is the floor, whatever the second number says.
   */
  if (mood <= 1 || (mood <= 2 && energy <= 2)) {
    return {
      key: "floor",
      headline: "Small is the point today",
      why: "On a day like this, starting is the whole goal. Anything past that is a bonus, not the bar.",
      ceiling: 10,
      options: FLOOR,
    };
  }

  if (mood <= 2 && energy >= 4) {
    return {
      key: "discharge",
      headline: "Something to burn off",
      why: "Low and restless is a different day from low and flat — this one usually eases once you've moved hard for a bit.",
      ceiling: 20,
      options: DISCHARGE,
    };
  }

  if (mood <= 3 && energy <= 3) {
    return {
      key: "easy",
      headline: "Keep it light",
      why: "Flat days respond better to something small and finished than to something ambitious and abandoned.",
      ceiling: 20,
      options: EASY,
    };
  }

  if (mood >= 4 && energy >= 4) {
    return {
      key: "capacity",
      headline: "You've got room today",
      why: "Good days are worth spending on the things that are hard to reach from a low one.",
      ceiling: 60,
      options: CAPACITY,
    };
  }

  return {
    key: "standard",
    headline: "A normal one",
    why: "Nothing special needed. Pick the one you're least likely to talk yourself out of.",
    ceiling: 30,
    options: STANDARD,
  };
}

/**
 * The one-line version, for surfaces that have room for a phrase and not a menu
 * (the clinician dashboard, a notification, the commitment record).
 */
export function activationSummary(mood, energy) {
  const a = activationFor(mood, energy);
  return a ? a.headline : null;
}

/**
 * Did the activation help THIS TIME?
 *
 * Deliberately three-valued and deliberately not a percentage. A single session
 * is an anecdote; the honest reading of one +1 is "that one helped", not "this
 * works for you". The aggregate claim belongs to the evidence engine, which
 * refuses to make it below three pairs.
 */
export function readSingleDelta(before, after) {
  if (before == null || after == null) return null;
  const delta = after - before;

  // The verbs are deliberately intransitive. "You came away better than you
  // went in" attributes the change to the activation, from a single pair, with
  // no comparison class — the app has no idea whether the afternoon would have
  // lifted them anyway, and its own three-pair rule forbids it from guessing.
  // What it actually knows is that two readings differ, so that is what it
  // says. The causal reading is the patient's to make, over time, from the
  // aggregate — which is the whole architecture of the product.
  if (delta > 0) {
    return { delta, direction: "up", line: "You're reading higher now than you did earlier." };
  }
  if (delta < 0) {
    return { delta, direction: "down", line: "Lower than earlier. Worth knowing — it goes in the record either way." };
  }
  return { delta: 0, direction: "flat", line: "Same as earlier. That happens, and it still counts as done." };
}
